/**
 * API service for LevelUp frontend
 * Axios instance with interceptors for authentication
 */

import axios, { AxiosInstance, AxiosError } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001/api/v1';

class ApiService {
  private api: AxiosInstance;

  /** Expose the configured axios instance for feature-level API modules. */
  get client(): AxiosInstance {
    return this.api;
  }

  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (error: unknown) => void;
  }> = [];

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor - add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - handle 401 with token refresh
    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as typeof error.config & { _retry?: boolean };

        if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
          // Skip refresh for auth endpoints themselves
          if (originalRequest.url?.includes('/auth/refresh') || originalRequest.url?.includes('/auth/login')) {
            return Promise.reject(error);
          }

          if (this.isRefreshing) {
            // Queue this request while refresh is in progress
            return new Promise((resolve, reject) => {
              this.failedQueue.push({
                resolve: (token: string) => {
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                  resolve(this.api.request(originalRequest));
                },
                reject,
              });
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          const refreshToken = localStorage.getItem('refresh_token');
          if (!refreshToken) {
            this.forceLogout();
            return Promise.reject(error);
          }

          try {
            const response = await this.refreshToken(refreshToken);
            const newToken = response.access_token;
            localStorage.setItem('access_token', newToken);

            // Retry queued requests
            this.failedQueue.forEach(({ resolve }) => resolve(newToken));
            this.failedQueue = [];

            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return this.api.request(originalRequest);
          } catch {
            this.failedQueue.forEach(({ reject }) => reject(error));
            this.failedQueue = [];
            this.forceLogout();
            return Promise.reject(error);
          } finally {
            this.isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private forceLogout() {
    this.clearAuth();
    // Also clear Zustand persisted state to prevent ProtectedRoute letting user back in
    localStorage.removeItem('auth-storage');
    window.location.href = '/login';
  }

  // ==================== Auth Methods ====================

  async register(data: RegisterData) {
    const response = await this.api.post('/auth/register', data);
    this.setAuth(response.data);
    return response.data;
  }

  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await this.api.post('/auth/login', { email, password });
    const data = response.data as LoginResponse;
    // Only set auth if no 2FA required
    if (!data.requires_2fa && data.access_token) {
      this.setAuth(data as AuthResponse);
    }
    return data;
  }

  async login2FA(code: string, tempToken: string): Promise<AuthResponse> {
    const response = await this.api.post(
      '/auth/login/2fa',
      { code },
      { headers: { 'X-Temp-Token': tempToken } }
    );
    this.setAuth(response.data);
    return response.data;
  }

  async refreshToken(refreshToken: string) {
    const response = await this.api.post('/auth/refresh', { refresh_token: refreshToken });
    return response.data;
  }

  async logoutBackend(): Promise<void> {
    try {
      await this.api.post('/auth/logout');
    } catch {
      // Ignore errors, clear local auth anyway
    }
    this.clearAuth();
  }

  async logoutAll(): Promise<{ message: string }> {
    const response = await this.api.post('/auth/logout/all');
    return response.data;
  }

  logout() {
    this.clearAuth();
  }

  // ==================== Session Management ====================

  async getSessions(): Promise<{ sessions: Session[]; count: number }> {
    const response = await this.api.get('/auth/sessions');
    return response.data;
  }

  async revokeSession(sessionId: string): Promise<void> {
    await this.api.delete(`/auth/sessions/${sessionId}`);
  }

  // ==================== Two-Factor Authentication ====================

  async setup2FA(): Promise<TwoFactorSetup> {
    const response = await this.api.post('/auth/2fa/setup');
    return response.data;
  }

  async enable2FA(code: string): Promise<void> {
    await this.api.post('/auth/2fa/enable', { code });
  }

  async disable2FA(password: string, code: string): Promise<void> {
    await this.api.post('/auth/2fa/disable', { password, code });
  }

  async regenerateBackupCodes(code: string): Promise<string[]> {
    const response = await this.api.post('/auth/2fa/backup-codes', { code });
    return response.data;
  }

  // ==================== OAuth2 ====================

  async googleLogin(idToken: string): Promise<AuthResponse> {
    const response = await this.api.post('/auth/google', { id_token: idToken });
    this.setAuth(response.data);
    return response.data;
  }

  async appleLogin(idToken: string): Promise<AuthResponse> {
    const response = await this.api.post('/auth/apple', { id_token: idToken });
    this.setAuth(response.data);
    return response.data;
  }

  async facebookLogin(accessToken: string): Promise<AuthResponse> {
    const response = await this.api.post('/auth/facebook', { access_token: accessToken });
    this.setAuth(response.data);
    return response.data;
  }

  private setAuth(data: AuthResponse) {
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('refresh_token', data.refresh_token);
    localStorage.setItem('user', JSON.stringify(data.user));
  }

  private clearAuth() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  }

  // ==================== User Methods ====================

  async getCurrentUser(): Promise<User> {
    const response = await this.api.get('/users/me');
    return response.data;
  }

  async getUser(userId: number): Promise<User> {
    const response = await this.api.get(`/users/${userId}`);
    return response.data;
  }

  async getLeaderboard(params?: LeaderboardParams): Promise<LeaderboardUser[]> {
    const response = await this.api.get('/users/', { params });
    return response.data;
  }

  async searchUsers(query: string, limit = 20): Promise<User[]> {
    const response = await this.api.get('/users/search/', {
      params: { q: query, limit },
    });
    return response.data;
  }

  async updateProfile(data: UpdateProfileData): Promise<User> {
    const response = await this.api.patch('/users/me', data);
    localStorage.setItem('user', JSON.stringify(response.data));
    return response.data;
  }

  async uploadAvatar(file: File): Promise<User> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.api.post('/users/me/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    localStorage.setItem('user', JSON.stringify(response.data));
    return response.data;
  }

  async changeLanguage(lang: string): Promise<void> {
    await this.api.patch(`/users/me/language?lang=${lang}`);
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.api.post('/users/me/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  }

  async changeClass(characterClass: CharacterClass): Promise<ChangeClassResponse> {
    const response = await this.api.post('/users/me/change-class', { character_class: characterClass });
    localStorage.setItem('user', JSON.stringify(response.data));
    return response.data;
  }

  async deleteAccount(password: string): Promise<void> {
    await this.api.delete('/users/me', {
      data: { password, confirm: true },
    });
    this.clearAuth();
  }

  async sendVerificationEmail(): Promise<{ token?: string }> {
    const response = await this.api.post('/users/me/send-verification');
    return response.data;
  }

  async verifyEmail(token: string): Promise<void> {
    await this.api.post('/users/verify-email', { token });
  }

  async forgotPassword(email: string): Promise<{ token?: string }> {
    const response = await this.api.post('/users/forgot-password', { email });
    return response.data;
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await this.api.post('/users/reset-password', { token, new_password: newPassword });
  }

  // ==================== Daily Goals ====================

  async getTodayGoal(): Promise<TodayGoalResponse> {
    const response = await this.api.get('/daily-goals/today');
    return response.data;
  }

  async updateDailyProgress(data: UpdateProgressRequest): Promise<UpdateProgressResponse> {
    const response = await this.api.post('/daily-goals/progress', data);
    return response.data;
  }

  async getDailyGoalHistory(limit = 30): Promise<DailyGoalSummary[]> {
    const response = await this.api.get('/daily-goals/history', { params: { limit } });
    return response.data;
  }

  async getStreakInfo(): Promise<StreakInfo> {
    const response = await this.api.get('/daily-goals/streak');
    return response.data;
  }

  // ==================== Workouts ====================

  async saveWorkout(data: WorkoutCreate): Promise<SaveWorkoutResponse> {
    const response = await this.api.post('/workouts/', data);
    return response.data;
  }

  async getWorkouts(params?: WorkoutListParams): Promise<WorkoutSummary[]> {
    const response = await this.api.get('/workouts/', { params });
    return response.data;
  }

  async getWorkout(workoutId: number): Promise<WorkoutDetailResponse> {
    const response = await this.api.get(`/workouts/${workoutId}`);
    return response.data;
  }

  async getWorkoutStats(): Promise<WorkoutStatsResponse> {
    const response = await this.api.get('/workouts/stats');
    return response.data;
  }

  async getExerciseStats(exerciseType: string): Promise<ExerciseStats> {
    const response = await this.api.get(`/workouts/stats/${exerciseType}`);
    return response.data;
  }

  /**
   * Analyze workout video with 100% accurate backend pose detection.
   * Use this for maximum accuracy (processing takes 10-60 seconds).
   */
  async analyzeWorkoutVideo(
    video: File | Blob,
    exerciseType: string
  ): Promise<VideoAnalysisResponse> {
    const formData = new FormData();
    formData.append('video', video);
    formData.append('exercise_type', exerciseType);

    const response = await this.api.post('/workouts/analyze-video', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000, // 2 minutes timeout for video processing
    });
    return response.data;
  }

  /**
   * Analyze video AND save workout in one request.
   * Recommended for 100% accurate workout tracking.
   */
  async analyzeAndSaveWorkout(
    video: File | Blob,
    exerciseType: string,
    options?: { saveWorkout?: boolean; challengeId?: number }
  ): Promise<VideoAnalysisWithSaveResponse> {
    const formData = new FormData();
    formData.append('video', video);
    formData.append('exercise_type', exerciseType);
    formData.append('save_workout', String(options?.saveWorkout ?? true));
    if (options?.challengeId) {
      formData.append('challenge_id', String(options.challengeId));
    }

    const response = await this.api.post('/workouts/analyze-and-save', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
    return response.data;
  }

  // ==================== Challenges ====================

  async getChallenges(params?: ChallengeListParams): Promise<ChallengeListResponse> {
    const response = await this.api.get('/challenges/', { params });
    return response.data;
  }

  async getChallenge(challengeId: number): Promise<ChallengeDetailResponse> {
    const response = await this.api.get(`/challenges/${challengeId}`);
    return response.data;
  }

  async getMyChallenges(): Promise<ChallengeResponse[]> {
    const response = await this.api.get('/challenges/my');
    return response.data;
  }

  async getChallengeStats(): Promise<UserChallengeStats> {
    const response = await this.api.get('/challenges/stats');
    return response.data;
  }

  async createChallenge(data: ChallengeCreate): Promise<ChallengeResponse> {
    const response = await this.api.post('/challenges/', data);
    return response.data;
  }

  async joinChallenge(challengeId: number): Promise<JoinChallengeResponse> {
    const response = await this.api.post(`/challenges/${challengeId}/join`);
    return response.data;
  }

  async leaveChallenge(challengeId: number): Promise<{ success: boolean; message: string }> {
    const response = await this.api.post(`/challenges/${challengeId}/leave`);
    return response.data;
  }

  async submitChallengeResult(challengeId: number, data: SubmitResultRequest): Promise<SubmitResultResponse> {
    const response = await this.api.post(`/challenges/${challengeId}/submit`, data);
    return response.data;
  }

  async getChallengeLeaderboard(challengeId: number): Promise<ChallengeLeaderboardResponse> {
    const response = await this.api.get(`/challenges/${challengeId}/leaderboard`);
    return response.data;
  }

  // ==================== Achievements ====================

  async getAllAchievements(): Promise<Achievement[]> {
    const response = await this.api.get('/achievements/');
    return response.data;
  }

  async getMyAchievements(): Promise<UserAchievement[]> {
    const response = await this.api.get('/achievements/my');
    return response.data;
  }

  async getAchievementSummary(): Promise<AchievementSummary> {
    const response = await this.api.get('/achievements/summary');
    return response.data;
  }

  async getAchievementProgress(): Promise<AchievementProgress[]> {
    const response = await this.api.get('/achievements/progress');
    return response.data;
  }

  // ==================== Friends & Social ====================

  async getFriends(): Promise<FriendsListResponse> {
    const response = await this.api.get('/friends/');
    return response.data;
  }

  async sendFriendRequest(userId: number): Promise<FriendActionResponse> {
    const response = await this.api.post(`/friends/${userId}/request`);
    return response.data;
  }

  async acceptFriendRequest(userId: number): Promise<FriendActionResponse> {
    const response = await this.api.post(`/friends/${userId}/accept`);
    return response.data;
  }

  async rejectFriendRequest(userId: number): Promise<FriendActionResponse> {
    const response = await this.api.post(`/friends/${userId}/reject`);
    return response.data;
  }

  async removeFriend(userId: number): Promise<FriendActionResponse> {
    const response = await this.api.delete(`/friends/${userId}`);
    return response.data;
  }

  async getPendingReceivedRequests(): Promise<PendingListResponse> {
    const response = await this.api.get('/friends/requests/received');
    return response.data;
  }

  async getPendingSentRequests(): Promise<PendingListResponse> {
    const response = await this.api.get('/friends/requests/sent');
    return response.data;
  }

  async getActivityFeed(limit = 20, offset = 0): Promise<ActivityFeedResponse> {
    const response = await this.api.get('/friends/feed', { params: { limit, offset } });
    return response.data;
  }

  async getUserFeed(userId: number, limit = 20, offset = 0): Promise<ActivityFeedResponse> {
    const response = await this.api.get(`/friends/feed/${userId}`, { params: { limit, offset } });
    return response.data;
  }

  async getFriendProfile(userId: number): Promise<FriendProfile> {
    const response = await this.api.get(`/friends/profile/${userId}`);
    return response.data;
  }

  // ==================== Teams ====================

  async createTeam(data: TeamCreate): Promise<TeamResponse> {
    const response = await this.api.post('/teams/', data);
    return response.data;
  }

  async getTeams(limit = 20, offset = 0): Promise<TeamListResponse> {
    const response = await this.api.get('/teams/', { params: { limit, offset } });
    return response.data;
  }

  async getMyTeam(): Promise<TeamDetailResponse | null> {
    const response = await this.api.get('/teams/my');
    return response.data;
  }

  async getTeam(teamId: number): Promise<TeamDetailResponse> {
    const response = await this.api.get(`/teams/${teamId}`);
    return response.data;
  }

  async updateTeam(teamId: number, data: TeamUpdate): Promise<TeamResponse> {
    const response = await this.api.patch(`/teams/${teamId}`, data);
    return response.data;
  }

  async deleteTeam(teamId: number): Promise<TeamActionResponse> {
    const response = await this.api.delete(`/teams/${teamId}`);
    return response.data;
  }

  async joinTeam(teamId: number): Promise<TeamActionResponse> {
    const response = await this.api.post(`/teams/${teamId}/join`);
    return response.data;
  }

  async leaveTeam(teamId: number): Promise<TeamActionResponse> {
    const response = await this.api.post(`/teams/${teamId}/leave`);
    return response.data;
  }

  async kickMember(teamId: number, userId: number): Promise<TeamActionResponse> {
    const response = await this.api.post(`/teams/${teamId}/kick/${userId}`);
    return response.data;
  }

  async transferCaptain(teamId: number, userId: number): Promise<TeamActionResponse> {
    const response = await this.api.post(`/teams/${teamId}/transfer/${userId}`);
    return response.data;
  }

  async inviteToTeam(teamId: number, userId: number): Promise<TeamActionResponse> {
    const response = await this.api.post(`/teams/${teamId}/invite/${userId}`);
    return response.data;
  }

  async acceptTeamInvite(teamId: number): Promise<TeamActionResponse> {
    const response = await this.api.post(`/teams/${teamId}/invite/accept`);
    return response.data;
  }

  async declineTeamInvite(teamId: number): Promise<TeamActionResponse> {
    const response = await this.api.post(`/teams/${teamId}/invite/decline`);
    return response.data;
  }

  async getMyTeamInvites(): Promise<PendingTeamInvitesResponse> {
    const response = await this.api.get('/teams/invites');
    return response.data;
  }

  // ==================== Quests ====================

  async getMyQuests(): Promise<UserQuestListResponse> {
    const response = await this.api.get('/quests/');
    return response.data;
  }

  // ==================== Cached Leaderboards ====================

  async getCachedLeaderboard(
    type: 'global' | 'weekly' | 'monthly' | 'friends' | 'teams',
    params?: { limit?: number; offset?: number }
  ): Promise<CachedLeaderboardResponse> {
    const response = await this.api.get(`/leaderboards/${type}`, { params });
    return response.data;
  }

  async getCityLeaderboard(city: string, params?: { limit?: number; offset?: number }): Promise<CachedLeaderboardResponse> {
    const response = await this.api.get(`/leaderboards/city/${city}`, { params });
    return response.data;
  }

  async getAgeGroupLeaderboard(ageGroup: string, params?: { limit?: number; offset?: number }): Promise<CachedLeaderboardResponse> {
    const response = await this.api.get(`/leaderboards/age-group/${ageGroup}`, { params });
    return response.data;
  }

  // ==================== Team Stats ====================

  async getTeamStats(teamId: number): Promise<TeamStatsResponse> {
    const response = await this.api.get(`/teams/${teamId}/stats`);
    return response.data;
  }

  // ==================== City Battles ====================

  async getCurrentCityBattle(): Promise<CityBattleDetailResponse | null> {
    const response = await this.api.get('/city-battles/current');
    return response.data;
  }

  async getCityBattleHistory(limit = 10, offset = 0): Promise<CityBattleListResponse> {
    const response = await this.api.get('/city-battles/history', { params: { limit, offset } });
    return response.data;
  }

  async getCityBattle(battleId: number): Promise<CityBattleDetailResponse> {
    const response = await this.api.get(`/city-battles/${battleId}`);
    return response.data;
  }

  // ==================== Reports ====================

  async reportUser(data: ReportCreate): Promise<ReportActionResponse> {
    const response = await this.api.post('/reports/', data);
    return response.data;
  }

  // ==================== Admin ====================

  async getPendingReports(limit = 50, offset = 0): Promise<AdminReportListResponse> {
    const response = await this.api.get('/reports/admin/pending', { params: { limit, offset } });
    return response.data;
  }

  async reviewReport(reportId: number, data: AdminReviewRequest): Promise<ReportActionResponse> {
    const response = await this.api.post(`/reports/admin/${reportId}/review`, data);
    return response.data;
  }

  async getFlaggedUsers(limit = 50, offset = 0): Promise<FlaggedUserListResponse> {
    const response = await this.api.get('/reports/admin/flagged-users', { params: { limit, offset } });
    return response.data;
  }

  // ==================== Team Challenges ====================

  async createTeamChallenge(teamId: number, data: TeamChallengeCreate): Promise<TeamChallengeResponse> {
    const response = await this.api.post('/team-challenges/', data, { params: { team_id: teamId } });
    return response.data;
  }

  async acceptTeamChallenge(challengeId: number): Promise<TeamChallengeResponse> {
    const response = await this.api.post(`/team-challenges/${challengeId}/accept`);
    return response.data;
  }

  async contributeToTeamChallenge(challengeId: number, teamId: number, data: TeamChallengeContributeData): Promise<TeamChallengeContributionResponse> {
    const response = await this.api.post(`/team-challenges/${challengeId}/contribute`, data, { params: { team_id: teamId } });
    return response.data;
  }

  async getTeamChallenge(challengeId: number): Promise<TeamChallengeDetailResponse> {
    const response = await this.api.get(`/team-challenges/${challengeId}`);
    return response.data;
  }

  async getActiveTeamChallenges(teamId: number): Promise<TeamChallengeResponse[]> {
    const response = await this.api.get(`/team-challenges/team/${teamId}/active`);
    return response.data;
  }

  async getTeamChallengeHistory(teamId: number, limit = 20, offset = 0): Promise<TeamChallengeListResponse> {
    const response = await this.api.get(`/team-challenges/team/${teamId}/history`, { params: { limit, offset } });
    return response.data;
  }

  // ==================== Notifications ====================

  async getNotifications(limit = 50, offset = 0, unreadOnly = false): Promise<NotificationListResponse> {
    const response = await this.api.get('/notifications/', { params: { limit, offset, unread_only: unreadOnly } });
    return response.data;
  }

  async markNotificationRead(notificationId: number): Promise<{ success: boolean }> {
    const response = await this.api.post(`/notifications/${notificationId}/read`);
    return response.data;
  }

  async markAllNotificationsRead(): Promise<{ marked_read: number }> {
    const response = await this.api.post('/notifications/read-all');
    return response.data;
  }

  // ==================== Chat ====================

  async getChatHistory(roomType: string, targetId: string, limit = 50, offset = 0): Promise<ChatHistoryResponse> {
    const response = await this.api.get(`/chat/history/${roomType}/${targetId}`, { params: { limit, offset } });
    return response.data;
  }

  // ==================== Rating ====================

  async getRankHistory(limit = 50, offset = 0): Promise<RankHistoryListResponse> {
    const response = await this.api.get('/rating/history', { params: { limit, offset } });
    return response.data;
  }

  async getPeakRank(): Promise<PeakRankResponse> {
    const response = await this.api.get('/rating/peak');
    return response.data;
  }

  async getPromotionSeries(): Promise<PromotionSeriesResponse | null> {
    try {
      const response = await this.api.get('/rating/promotion-series');
      return response.data;
    } catch {
      return null;
    }
  }

  async getRatingInfo(): Promise<RatingInfoResponse> {
    const response = await this.api.get('/rating/info');
    return response.data;
  }

  // Helper for raw requests
  get instance() {
    return this.api;
  }

  // ==================== Spirit Pet ====================

  async getMyPet(): Promise<PetStatus> {
    const response = await this.api.get('/pets/me');
    return response.data;
  }

  async createPet(petType?: PetType): Promise<PetStatus> {
    const response = await this.api.post('/pets/me', petType ? { pet_type: petType } : {});
    return response.data;
  }

  async feedPet(): Promise<PetFeedResponse> {
    const response = await this.api.post('/pets/me/feed');
    return response.data;
  }

  async abandonPet(): Promise<{ success: boolean; message: string }> {
    const response = await this.api.delete('/pets/me');
    return response.data;
  }

  // ==================== Running/GPS Methods ====================

  async getRunningSessions(params?: RunningListParams): Promise<RunningSession[]> {
    const response = await this.api.get('/running/sessions', { params });
    return response.data;
  }

  async getRunningSession(sessionId: number): Promise<RunningSessionDetail> {
    const response = await this.api.get(`/running/sessions/${sessionId}`);
    return response.data;
  }

  async saveRunningSession(data: RunningCreate): Promise<SaveRunningResponse> {
    const response = await this.api.post('/running/sessions', data);
    return response.data;
  }

  async getRunningStats(): Promise<RunningStats> {
    const response = await this.api.get('/running/stats');
    return response.data;
  }

  // ==================== Maps/Mapbox Methods ====================

  async getMapStatus(): Promise<MapStatus> {
    const response = await this.api.get('/maps/status');
    return response.data;
  }

  async getStaticMapUrl(polyline: string, width?: number, height?: number): Promise<StaticMapResponse> {
    const response = await this.api.get('/maps/static-url', {
      params: { polyline, width, height },
    });
    return response.data;
  }

  async reverseGeocode(lat: number, lng: number): Promise<GeocodingResult> {
    const response = await this.api.get('/maps/geocode', { params: { lat, lng } });
    return response.data;
  }

  async getRouteGeoJSON(polyline: string): Promise<GeoJSONFeature> {
    const response = await this.api.get('/maps/geojson', { params: { polyline } });
    return response.data;
  }

  async getRouteBounds(polyline: string): Promise<RouteBounds> {
    const response = await this.api.get('/maps/bounds', { params: { polyline } });
    return response.data;
  }
}

// ==================== Types ====================

// Auth Types
export type CharacterClass = 'warrior' | 'runner' | 'guardian' | 'healer';

export interface RegisterData {
  email: string;
  username: string;
  password: string;
  date_of_birth: string;
  city?: string;
  country?: string;
  character_class?: CharacterClass;
}

export interface AuthResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  user: User;
}

export interface LoginResponse {
  access_token?: string;
  refresh_token?: string;
  token_type?: string;
  user?: User;
  requires_2fa: boolean;
  temp_token?: string;
}

export interface TwoFactorSetup {
  secret: string;
  qr_uri: string;
  backup_codes: string[];
}

export interface Session {
  session_id: string;
  device: string;
  created: string;
  is_current: boolean;
}

export interface User {
  id: number;
  email: string;
  username: string;
  age_group: 'teen' | 'young_adult' | 'adult' | 'middle_age' | 'senior';
  city?: string;
  country: string;
  level: number;
  xp: number;
  current_streak: number;
  longest_streak: number;
  mmr: number;
  league: string;
  division: number;
  total_workouts: number;
  total_reps: number;
  is_premium: boolean;
  is_verified?: boolean;
  avatar_url?: string;
  bio?: string;
  preferred_language?: string;
  created_at: string;
  character_class?: CharacterClass;
  class_changed_at?: string;
}

export interface LeaderboardUser extends User {
  rank?: number;
}

export interface LeaderboardParams {
  limit?: number;
  offset?: number;
  age_group?: string;
  city?: string;
  country?: string;
}

export interface UpdateProfileData {
  username?: string;
  city?: string;
  bio?: string;
  avatar_url?: string;
}

export interface ChangeClassResponse {
  character_class: CharacterClass;
  next_change_available: string;
}

// Daily Goal Types
export interface TodayGoalResponse {
  id: number;
  date: string;
  age_group: string;
  age_group_description: string;
  exercises: ExerciseDetail[];
  overall_completion: number;
  completed: boolean;
  streak_eligible: boolean;
  current_streak: number;
  xp_multiplier: number;
}

export interface ExerciseDetail {
  exercise: string;
  display_name: string;
  target: number;
  completed: number;
  completion_percent: number;
  is_complete: boolean;
  form_avg: number;
}

export interface UpdateProgressRequest {
  exercise: string;
  reps: number;
  form_score?: number;
}

export interface UpdateProgressResponse {
  exercise: string;
  target: number;
  completed: number;
  form_avg: number;
  completion_percentage: number;
  is_exercise_complete: boolean;
  is_goal_complete: boolean;
  streak_eligible: boolean;
  xp_earned: number;
}

export interface DailyGoalSummary {
  id: number;
  date: string;
  completion_percentage: number;
  completed: boolean;
  streak_eligible: boolean;
}

export interface StreakInfo {
  current_streak: number;
  longest_streak: number;
  streak_eligible_today: boolean;
  days_until_next_milestone: number;
  next_milestone: number;
  next_milestone_reward_xp: number;
}

// Workout Types
export interface WorkoutCreate {
  exercise_type: string;
  total_reps: number;
  duration_seconds: number;
  form_score_avg?: number;
  verification_snapshots?: Record<string, unknown>[];
  session_data?: Record<string, unknown>;
  challenge_id?: number;
}

export interface WorkoutSummary {
  id: number;
  exercise_type: string;
  total_reps: number;
  duration_seconds: number;
  form_score_avg?: number;
  is_personal_record: boolean;
  created_at: string;
}

export interface WorkoutDetailResponse extends WorkoutSummary {
  user_id: number;
  calories_burned?: number;
  verification_snapshots?: Record<string, unknown>[];
  session_data?: Record<string, unknown>;
  challenge_id?: number;
  reps_per_minute: number;
  completed_at?: string;
}

export interface SaveWorkoutResponse {
  workout: WorkoutDetailResponse;
  xp_earned: number;
  is_personal_record: boolean;
  daily_goal_updated: boolean;
  new_level?: number;
  achievements_unlocked: string[];
}

export interface WorkoutListParams {
  limit?: number;
  offset?: number;
  exercise_type?: string;
}

export interface PersonalRecordResponse {
  exercise_type: string;
  max_reps?: number;
  best_time_for_100?: number;
  best_form_score?: number;
  total_reps_lifetime: number;
  total_sessions: number;
  updated_at: string;
}

export interface WorkoutStatsResponse {
  total_workouts: number;
  total_reps: number;
  total_duration_seconds: number;
  total_calories_burned: number;
  avg_form_score: number;
  favorite_exercise?: string;
  personal_records: PersonalRecordResponse[];
}

export interface ExerciseStats {
  exercise_type: string;
  total_sessions: number;
  total_reps: number;
  avg_reps_per_session: number;
  avg_form_score: number;
  best_session_reps: number;
  best_form_score: number;
}

// Challenge Types
export interface ChallengeListParams {
  limit?: number;
  offset?: number;
  status?: 'draft' | 'upcoming' | 'active' | 'completed' | 'cancelled';
  challenge_type?: 'speed' | 'volume' | 'accuracy' | 'combo';
  exercise_type?: string;
  visibility?: 'public' | 'private' | 'city' | 'friends';
  city?: string;
  age_group?: string;
}

export interface ChallengeResponse {
  id: number;
  title: string;
  description?: string;
  challenge_type: string;
  exercise_type: string;
  goal_value?: number;
  time_limit_seconds?: number;
  start_date: string;
  end_date: string;
  creator_id: number;
  visibility: string;
  city?: string;
  country?: string;
  max_participants?: number;
  min_age?: number;
  max_age?: number;
  age_group?: string;
  is_rated: boolean;
  division?: string;
  chat_enabled: boolean;
  status: string;
  participant_count?: number;
  created_at: string;
}

export interface ChallengeDetailResponse extends ChallengeResponse {
  xp_reward_1st: number;
  xp_reward_2nd: number;
  xp_reward_3rd: number;
  xp_reward_participation: number;
  winner_id?: number;
  results_summary?: Record<string, unknown>;
  updated_at?: string;
  creator_username?: string;
}

export interface ChallengeListResponse {
  challenges: ChallengeResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface ChallengeCreate {
  title: string;
  description?: string;
  challenge_type?: string;
  exercise_type: string;
  goal_value?: number;
  time_limit_seconds?: number;
  start_date: string;
  end_date: string;
  visibility?: string;
  city?: string;
  country?: string;
  max_participants?: number;
  min_age?: number;
  max_age?: number;
  age_group?: string;
  is_rated?: boolean;
  division?: string;
  chat_enabled?: boolean;
  xp_reward_1st?: number;
  xp_reward_2nd?: number;
  xp_reward_3rd?: number;
  xp_reward_participation?: number;
}

export interface JoinChallengeResponse {
  success: boolean;
  message: string;
  challenge_id: number;
  joined_at: string;
}

export interface SubmitResultRequest {
  workout_id: number;
  total_reps: number;
  time_seconds?: number;
  form_score?: number;
}

export interface SubmitResultResponse {
  success: boolean;
  message: string;
  current_rank?: number;
  total_reps: number;
  best_time_seconds?: number;
  form_score_avg?: number;
}

export interface ChallengeLeaderboardEntry {
  rank: number;
  user_id: number;
  username: string;
  avatar_url?: string;
  age_group?: string;
  total_reps: number;
  best_time_seconds?: number;
  form_score_avg?: number;
  xp_earned: number;
  mmr_change: number;
}

export interface ChallengeLeaderboardResponse {
  challenge_id: number;
  challenge_title: string;
  status: string;
  entries: ChallengeLeaderboardEntry[];
  total_participants: number;
  user_rank?: number;
}

export interface UserChallengeStats {
  total_participated: number;
  total_won: number;
  total_top3: number;
  total_xp_earned: number;
  total_mmr_gained: number;
  favorite_exercise?: string;
  best_percentile?: number;
}

// Video Analysis Types (100% accurate backend processing)
export interface VideoAnalysisResponse {
  total_reps: number;
  form_score_avg: number;
  form_scores: number[];
  duration_seconds: number;
  frames_processed: number;
  landmarks_detected: number;
  exercise_type: string;
  feedback_summary: string[];
  detection_rate: number;
}

export interface VideoAnalysisWithSaveResponse {
  analysis: VideoAnalysisResponse;
  workout: SaveWorkoutResponse | null;
  saved: boolean;
}

// Achievement Types
export interface Achievement {
  id: number;
  code: string;
  name: string;
  description: string;
  category: 'workout' | 'streak' | 'challenge' | 'social' | 'milestone' | 'exercise';
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  icon: string;
  xp_reward: number;
  unlock_condition: Record<string, unknown>;
  is_hidden: boolean;
  sort_order: number;
}

export interface UserAchievement {
  id: number;
  achievement: Achievement;
  unlocked_at: string;
  progress_snapshot?: Record<string, unknown>;
}

export interface AchievementSummary {
  total_achievements: number;
  unlocked_count: number;
  completion_percentage: number;
  total_xp_from_achievements: number;
  by_category: Record<string, { total: number; unlocked: number }>;
  by_rarity: Record<string, { total: number; unlocked: number }>;
  recent_unlocks: UserAchievement[];
}

export interface AchievementProgress {
  achievement: Achievement;
  unlocked: boolean;
  unlocked_at?: string;
  current_progress: number;
  target_progress: number;
  progress_percentage: number;
}

// Friends & Social Types
export interface FriendInfo {
  id: number;
  username: string;
  avatar_url?: string;
  level: number;
  league: string;
  division: number;
  current_streak: number;
}

export interface FriendsListResponse {
  friends: FriendInfo[];
  count: number;
}

export interface PendingRequest {
  id: number;
  user: FriendInfo;
  created_at: string;
}

export interface PendingListResponse {
  requests: PendingRequest[];
  count: number;
}

export interface FriendActionResponse {
  success: boolean;
  message: string;
}

export interface FriendProfile {
  id: number;
  username: string;
  avatar_url?: string;
  bio?: string;
  city?: string;
  level: number;
  xp: number;
  league: string;
  division: number;
  mmr: number;
  current_streak: number;
  longest_streak: number;
  total_workouts: number;
  total_reps: number;
  created_at: string;
  total_runs: number;
  total_distance_meters: number;
  total_run_duration_seconds: number;
  longest_run_meters: number;
  best_pace_seconds_per_km: number | null;
  total_calories_burned: number;
  avg_form_score: number | null;
  favorite_exercise: string | null;
  recent_activities: { id: number; type: string; data: Record<string, unknown>; created_at: string }[];
}

export interface ActivityUser {
  id: number;
  username: string;
  avatar_url?: string;
}

export interface ActivityItem {
  id: number;
  user: ActivityUser;
  activity_type: string;
  data: Record<string, unknown>;
  created_at: string;
}

export interface ActivityFeedResponse {
  activities: ActivityItem[];
  count: number;
  has_more: boolean;
}

// Team Types
export interface TeamMemberInfo {
  id: number;
  username: string;
  avatar_url?: string;
  level: number;
  role: string;
  joined_at: string;
}

export interface TeamCreate {
  name: string;
  description?: string;
  is_public?: boolean;
  max_members?: number;
}

export interface TeamUpdate {
  name?: string;
  description?: string;
  avatar_url?: string;
  is_public?: boolean;
  max_members?: number;
}

export interface TeamResponse {
  id: number;
  name: string;
  description?: string;
  captain_id?: number;
  avatar_url?: string;
  team_mmr: number;
  team_league: string;
  total_wins: number;
  total_challenges: number;
  is_public: boolean;
  max_members: number;
  member_count: number;
  created_at: string;
}

export interface TeamDetailResponse extends TeamResponse {
  members: TeamMemberInfo[];
  captain_username?: string;
}

export interface TeamListResponse {
  teams: TeamResponse[];
  total: number;
}

export interface TeamActionResponse {
  success: boolean;
  message: string;
}

export interface TeamInvite {
  id: number;
  team_id: number;
  team_name: string;
  invited_by_username: string;
  created_at: string;
}

export interface PendingTeamInvitesResponse {
  invites: TeamInvite[];
  count: number;
}

// Running Types
export type ActivityType = 'running' | 'walking' | 'cycling' | 'hiking';

export interface RunningListParams {
  limit?: number;
  offset?: number;
  activity_type?: ActivityType;
}

export interface RunningSession {
  id: number;
  user_id: number;
  activity_type: ActivityType;
  distance_meters: number;
  duration_seconds: number;
  avg_pace_seconds_per_km: number | null;
  elevation_gain_meters: number;
  calories_burned: number | null;
  is_personal_record: boolean;
  started_at: string;
  title: string | null;
  static_map_url: string | null;
  location_name: string | null;
}

export interface RunningSessionDetail extends RunningSession {
  route_polyline: string;
  start_lat: number;
  start_lng: number;
  end_lat: number;
  end_lng: number;
  avg_heart_rate: number | null;
  splits: Split[] | null;
  xp_earned: number;
  pr_type: string | null;
  is_public: boolean;
  completed_at: string;
}

export interface Split {
  km: number;
  pace_seconds: number;
  elevation_change: number;
}

export interface RunningCreate {
  activity_type: ActivityType;
  route_polyline: string;
  distance_meters: number;
  duration_seconds: number;
  elevation_gain_meters?: number;
  started_at: string;
  completed_at: string;
  splits?: Split[];
  avg_heart_rate?: number;
  title?: string;
  is_public?: boolean;
}

export interface SaveRunningResponse {
  session: RunningSession;
  xp_earned: number;
  is_personal_record: boolean;
  pr_type: string | null;
  daily_goal_updated: boolean;
}

export interface RunningStats {
  total_runs: number;
  total_distance_meters: number;
  total_duration_seconds: number;
  total_elevation_gain: number;
  avg_pace_seconds_per_km: number | null;
  longest_run_meters: number;
  best_pace_seconds_per_km: number | null;
}

// Maps Types
export interface MapStatus {
  configured: boolean;
  style: string | null;
}

export interface StaticMapResponse {
  url: string;
  bounds: RouteBounds;
}

export interface GeocodingResult {
  place: string | null;
  city: string | null;
  country: string | null;
  full: string;
}

export interface RouteBounds {
  sw: [number, number]; // [lng, lat]
  ne: [number, number]; // [lng, lat]
}

export interface GeoJSONFeature {
  type: 'Feature';
  geometry: {
    type: 'LineString';
    coordinates: [number, number][];
  };
  properties: Record<string, unknown>;
}

// Quest Types
export interface QuestInfo {
  id: number;
  code: string;
  title: string;
  description: string;
  quest_type: 'daily' | 'weekly';
  category: string;
  requirement_type: string;
  requirement_value: number;
  exercise_type: string | null;
  xp_reward: number;
}

export interface UserQuestItem {
  id: number;
  quest: QuestInfo;
  status: 'active' | 'completed' | 'expired';
  progress: number;
  assigned_at: string;
  completed_at: string | null;
  expires_at: string;
  progress_percent: number;
}

export interface UserQuestListResponse {
  quests: UserQuestItem[];
  daily_completed: number;
  weekly_completed: number;
}

// Cached Leaderboard Types
export interface CachedLeaderboardEntry {
  user_id?: number;
  team_id?: number;
  username?: string;
  name?: string;
  avatar_url: string | null;
  mmr?: number;
  team_mmr?: number;
  league?: string;
  team_league?: string;
  division?: number;
  level?: number;
  age_group?: string;
  city?: string;
  rank: number;
  weekly_xp?: number;
  monthly_xp?: number;
  member_count?: number;
}

export interface CachedLeaderboardResponse {
  entries: CachedLeaderboardEntry[];
  cached: boolean;
}

// Team Stats Types
export interface TeamStatsResponse {
  team_id: number;
  team_name: string;
  team_mmr: number;
  team_league: string;
  member_count: number;
  age_diversity_bonus: number;
  unique_age_groups: number;
  total_team_xp: number;
  total_team_workouts: number;
}

// City Battle Types
export interface CityScore {
  city: string;
  xp: number;
  participants: number;
  workouts: number;
  rank: number;
}

export interface ContributionResponse {
  user_id: number;
  username: string;
  xp_earned: number;
  workouts_count: number;
  reps_count: number;
}

export interface CityBattleResponse {
  id: number;
  week_number: number;
  year: number;
  start_date: string;
  end_date: string;
  status: string;
  winner_city: string | null;
  city_scores: CityScore[];
  created_at: string;
}

export interface CityBattleDetailResponse extends CityBattleResponse {
  user_contribution: ContributionResponse | null;
  top_contributors: ContributionResponse[];
}

export interface CityBattleListResponse {
  battles: CityBattleResponse[];
  total: number;
}

// Report Types
export interface ReportCreate {
  reported_user_id: number;
  reason: 'cheating' | 'unrealistic_performance' | 'fake_workout' | 'abusive_behavior' | 'other';
  description?: string;
  evidence_workout_id?: number;
}

export interface ReportActionResponse {
  success: boolean;
  message: string;
}

// Admin Types
export interface ReportDetailResponse {
  id: number;
  reporter_id: number;
  reported_user_id: number;
  reason: string;
  description: string | null;
  evidence_workout_id: number | null;
  status: string;
  created_at: string;
  reporter_username: string;
  reported_username: string;
  admin_notes: string | null;
  reviewed_at: string | null;
}

export interface AdminReportListResponse {
  reports: ReportDetailResponse[];
  total: number;
}

export interface AdminReviewRequest {
  action: 'dismiss' | 'warn' | 'ban';
  admin_notes?: string;
  ban_reason?: string;
}

export interface FlaggedUserResponse {
  id: number;
  username: string;
  trust_score: number;
  total_reports: number;
  is_banned: boolean;
  city: string | null;
  created_at: string;
}

export interface FlaggedUserListResponse {
  users: FlaggedUserResponse[];
  total: number;
}

// Rating Types
export interface RankHistoryEntry {
  id: number;
  change_type: string;
  mmr_before: number;
  mmr_after: number;
  league_before: string;
  league_after: string;
  division_before: number;
  division_after: number;
  lp_before: number;
  lp_after: number;
  challenge_id: number | null;
  created_at: string;
}

export interface RankHistoryListResponse {
  history: RankHistoryEntry[];
  total: number;
}

export interface PromotionSeriesResponse {
  id: number;
  status: string;
  from_league: string;
  from_division: number;
  to_league: string;
  to_division: number;
  results: string[];
  wins: number;
  losses: number;
  created_at: string;
  completed_at: string | null;
}

export interface PeakRankResponse {
  peak_mmr: number;
  peak_league: string;
  peak_division: number;
  current_mmr: number;
  current_league: string;
  current_division: number;
}

export interface RatingInfoResponse {
  mmr: number;
  league: string;
  division: number;
  lp: number;
  rank_in_league: number;
  total_in_league: number;
  peak_mmr: number;
  peak_league: string;
  peak_division: number;
}

// Team Challenge Types
export interface TeamChallengeCreate {
  title: string;
  exercise_type: string;
  goal_reps: number;
  opponent_team_id: number;
  start_date: string;
  end_date: string;
  is_rated?: boolean;
}

export interface TeamChallengeResponse {
  id: number;
  title: string;
  exercise_type: string;
  goal_reps: number;
  challenger_team_id: number;
  challenger_team_name: string | null;
  opponent_team_id: number;
  opponent_team_name: string | null;
  start_date: string;
  end_date: string;
  status: string;
  is_rated: boolean;
  challenger_score: number;
  opponent_score: number;
  winner_team_id: number | null;
  created_at: string;
}

export interface TeamChallengeContributeData {
  reps: number;
  form_score_avg?: number;
}

export interface TeamChallengeContributionResponse {
  id: number;
  user_id: number;
  username: string | null;
  team_id: number;
  reps: number;
  form_score_avg: number | null;
}

export interface TeamChallengeDetailResponse extends TeamChallengeResponse {
  contributions: TeamChallengeContributionResponse[];
}

export interface TeamChallengeListResponse {
  challenges: TeamChallengeResponse[];
  total: number;
}

// Notification Types
export interface NotificationItem {
  id: number;
  type: string;
  title: string;
  body: string | null;
  is_read: boolean;
  action_url: string | null;
  reference_id: number | null;
  reference_type: string | null;
  created_at: string;
}

export interface NotificationListResponse {
  notifications: NotificationItem[];
  unread_count: number;
  total: number;
}

// Chat Types
export interface ChatMessage {
  id: number;
  room_type: string;
  room_id: string;
  sender_id: number;
  sender_username: string;
  content: string;
  created_at: string;
}

export interface ChatHistoryResponse {
  messages: ChatMessage[];
  room_id: string;
}

// Pet Types
export type PetType = 'fire_wolf' | 'wind_eagle' | 'stone_bear' | 'nature_fox';
export type PetState = 'healthy' | 'hungry' | 'sad' | 'weakened';

export interface PetStatus {
  id: number;
  pet_type: PetType;
  evolution_stage: number;
  xp: number;
  last_fed_date: string;
  created_at: string;
  hunger: number;
  happiness: number;
  state: PetState;
  days_since_fed: number;
  xp_to_next_stage: number;
}

export interface PetFeedResponse {
  success: boolean;
  message: string;
  xp_gained: number;
  pet: PetStatus;
}

// Export singleton instance
export const api = new ApiService();

/**
 * Extract a human-readable error message from API errors.
 * Handles FastAPI validation errors (422) where detail is an array of objects.
 */
export function getErrorMessage(error: any, fallback = 'Something went wrong'): string {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e: any) => e.msg || String(e)).join(', ');
  }
  return fallback;
}
